"use client";

import { useState, useEffect } from "react";

// Вспомогательные функции для работы с cookies
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

function setCookie(name: string, value: string, days: number = 365) {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  // Определяем, находимся ли мы на production домене
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProduction = hostname.includes('investmentbastion.com');
  
  // В production используем domain для всех поддоменов, в development - без domain
  const domainOption = isProduction ? `domain=.investmentbastion.com;` : '';
  
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;${domainOption}SameSite=Lax`;
}

// Функция для получения значения cookie consent (проверяет и cookie, и localStorage для обратной совместимости)
function getCookieConsent(): string | null {
  // Сначала проверяем cookie (новый способ)
  const cookieValue = getCookie("cookieConsent");
  if (cookieValue) return cookieValue;
  
  // Если cookie нет, проверяем localStorage (для обратной совместимости)
  if (typeof window !== 'undefined') {
    const localStorageValue = localStorage.getItem("cookieConsent");
    if (localStorageValue) {
      // Мигрируем значение из localStorage в cookie
      setCookie("cookieConsent", localStorageValue);
      return localStorageValue;
    }
  }
  
  return null;
}

export default function CookieConsent() {
  const hasOptionalCookies = false; // Set to true when optional cookies (analytics, marketing) are added
  
  // Если используются только строго необходимые cookies, баннер не требуется
  // (Strictly Necessary Cookies не требуют согласия по GDPR/PECR)
  if (!hasOptionalCookies) {
    return null;
  }

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = getCookieConsent();
    if (!cookieConsent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setCookie("cookieConsent", "accepted_all");
    // Также сохраняем в localStorage для обратной совместимости
    if (typeof window !== 'undefined') {
      localStorage.setItem("cookieConsent", "accepted_all");
    }
    setShowBanner(false);
  };

  const handleRejectOptional = () => {
    setCookie("cookieConsent", "accepted_necessary");
    if (typeof window !== 'undefined') {
      localStorage.setItem("cookieConsent", "accepted_necessary");
    }
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    setCookie("cookieConsent", "accepted_necessary");
    if (typeof window !== 'undefined') {
      localStorage.setItem("cookieConsent", "accepted_necessary");
    }
    setShowBanner(false);
  };

  const handleManageSettings = () => {
    // For now, just accept necessary. In the future, this could open a settings modal
    handleAcceptNecessary();
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[99999] p-4 md:p-6"
      style={{
        background: "rgba(18, 18, 18, 0.98)",
        backdropFilter: "blur(10px)",
        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.5)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-white/90 leading-relaxed">
              We use only essential cookies to ensure the best experience on Investment Bastion.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {hasOptionalCookies ? (
              <>
                <button
                  onClick={handleRejectOptional}
                  className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors rounded-lg border border-white/20 hover:border-white/30"
                  style={{
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  Reject optional
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 text-sm font-semibold text-black bg-[#42db7b] hover:bg-[#3bc56b] rounded-lg transition-colors"
                  style={{
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  Accept all
                </button>
                <button
                  onClick={handleManageSettings}
                  className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
                  style={{
                    fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  }}
                >
                  Manage settings
                </button>
              </>
            ) : (
              <button
                onClick={handleAcceptNecessary}
                className="px-6 py-2 text-sm font-semibold text-black bg-[#42db7b] hover:bg-[#3bc56b] rounded-lg transition-colors"
                style={{
                  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                }}
              >
                Accept necessary cookies
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

